import { CommonModule } from '@angular/common'
import { Component, ElementRef, OnInit, inject, viewChild } from '@angular/core'

import { Store } from '@ngrx/store'
import { DialogModule } from 'primeng/dialog'
import { ImageModule } from 'primeng/image'
import { ProgressBarModule } from 'primeng/progressbar'
import { TooltipModule } from 'primeng/tooltip'

import { AiCoordinatorActions } from '@flowx/designer/modules/new-ai/agents/coordinator/store/coordinator.actions'
import { DocumentsService } from '@flowx/designer/modules/new-ai/agents/documents/api/documents.service'
import { ContextDocument } from '@flowx/designer/modules/new-ai/common/ai.dictionary'
import { resetInputFileValueFromEvent } from '@flowx/designer/modules/new-ai/common/ai.utils'
import { FlxAiMarkdownPipe } from '@flowx/designer/modules/new-ai/common/pipes/flx-ai-markdown.pipe'
import { AiActions } from '@flowx/designer/modules/new-ai/store/ai.actions'
import { AiSelectors } from '@flowx/designer/modules/new-ai/store/ai.selectors'

import { FlxLogDateFormatPipe } from '../agent-log/agent-log.component'

@Component({
  selector: 'flx-ai-agent-documents',
  imports: [
    CommonModule,
    DialogModule,
    ImageModule,
    ProgressBarModule,
    TooltipModule,
    FlxLogDateFormatPipe,
    FlxAiMarkdownPipe,
  ],
  templateUrl: './agent-documents.component.html',
  styleUrls: ['./agent-documents.component.scss'],
  standalone: true,
})
export class AgentDocumentsComponent implements OnInit {
  #store = inject(Store)
  #documentsService = inject(DocumentsService)

  documents = this.#store.selectSignal(AiSelectors.currentDocuments)
  isUploadingDocuments = this.#store.selectSignal(AiSelectors.isUploadingDocuments)
  allowedDocumentFileTypes = this.#store.selectSignal(AiSelectors.allowedDocumentFileTypes)
  pageAgent = this.#store.selectSignal(AiSelectors.pageAgent)
  isMultipleDocumentUploadAllowed = this.#store.selectSignal(
    AiSelectors.isMultipleDocumentUploadAllowed
  )
  describeDocument: ContextDocument | null = null
  docUpload = viewChild<ElementRef>('docUpload')
  appId = this.#store.selectSignal(AiSelectors.getAppId)
  appVersionId = this.#store.selectSignal(AiSelectors.getAppVersionId)
  sessionId = this.#store.selectSignal(AiSelectors.sessionId)

  getIconByType(type: string): string {
    switch (type) {
      case 'node':
        return 'ph pt-1 text-yellow-500 ph-tree-structure font-bold'
      case 'action':
        return 'ph pt-1 text-yellow-500 ph-code font-bold'
      case 'ui':
        return 'ph pt-1 text-yellow-500 ph-layout font-bold'
      case 'datamodel':
        return 'ph pt-1 text-yellow-500 ph-database font-bold'
      default:
        return 'document'
    }
  }

  ngOnInit(): void {
    this.checkContextDocuments()
  }

  toggleDescription(doc: ContextDocument): void {
    if (this.describeDocument?.id === doc.id) {
      this.describeDocument = null
      return
    } else {
      this.describeDocument = doc
    }
  }

  uploadDocument(): void {
    this.docUpload().nativeElement.click()
  }
  getDocumentType(documentType: string): string {
    switch (documentType) {
      case 'api_specification':
        return 'API Specification'
      case 'business_process_definition':
        return 'Business Process Definition'
      case 'compliance_checks_definition':
        return 'Compliance Checks Definition'
      default:
        return 'Unknown Format'
    }
  }

  getDocumentTypeIcon(documentType: string): string {
    switch (documentType) {
      case 'api_specification':
        return 'ph ph-code font-bold'
      case 'business_process_definition':
        return 'ph ph-tree-structure font-bold'
      case 'compliance_checks_definition':
        return 'ph ph-check-circle font-bold'
      default:
        return 'ph ph-file-pdf font-bold'
    }
  }
  getDocumentTypeColor(documentType: string): string {
    switch (documentType) {
      case 'api_specification':
        return 'bg-blue-800'
      case 'business_process_definition':
        return 'bg-yellow-800'
      case 'compliance_checks_definition':
        return 'bg-orange-800'
      default:
        return 'bg-gray-800'
    }
  }

  getStatusColor(status: string): string {
    switch (status) {
      case 'Evaluated':
        return 'bg-green-500'
      default:
        return 'bg-neutral-600'
    }
  }

  onFileSelected(event: Event): void {
    this.#store.dispatch(AiActions.fileUploaded({ event }))
    resetInputFileValueFromEvent(event)
    this.#store.dispatch(AiActions.setDocumentsSidebarVisible({ isDocumentsSidebarVisible: false }))
  }

  checkContextDocuments(): void {
    this.#store.dispatch(AiActions.fromDocumentsClick())
  }
  useDocument(doc: ContextDocument): void {
    this.#store.dispatch(
      AiActions.selectExtractedDocument({ document: doc, pageAgent: this.pageAgent() })
    )
    this.#store.dispatch(AiActions.setDocumentsSidebarVisible({ isDocumentsSidebarVisible: false }))
    this.describeDocument = null
  }

  generatePlanFromDocument(doc: ContextDocument): void {
    this.#store.dispatch(
      AiActions.selectExtractedDocument({ document: doc, pageAgent: this.pageAgent() })
    )
    this.#store.dispatch(AiActions.setDocumentsSidebarVisible({ isDocumentsSidebarVisible: false }))
    this.#store.dispatch(
      AiCoordinatorActions.generatePlan({
        prompt: 'Generate a comprehensive plan from the uploaded document: ' + doc.name,
        appId: this.appId(),
        appVersion: this.appVersionId(),
        plan_type: 'application',
        current_plan: null,
        context: null,
        session_id: this.sessionId(),
        document_id: doc.documentId,
      })
    )
    this.describeDocument = null
  }

  async openPdf(doc: ContextDocument): Promise<void> {
    if (!doc.storagePath || !doc.resourceId) {
      return
    }
    const result = await this.#documentsService.getImageContent(
      doc.storagePath,
      doc.resourceId,
      doc.type || 'application/pdf'
    )
    if (!result) {
      return
    }
    const byteChars = atob(result.content)
    const byteArray = new Uint8Array(byteChars.length)
    for (let i = 0; i < byteChars.length; i++) {
      byteArray[i] = byteChars.charCodeAt(i)
    }
    const blob = new Blob([byteArray], { type: result.content_type || 'application/pdf' })
    const url = URL.createObjectURL(blob)
    window.open(url, '_blank')
  }
}
